import SharedUI, { NameHooks } from '@portal/shared-ui'
import PortalHelper, {
  Context,
  rewriteWebpackPublicPath
} from '@portal/vue-helper'
import ElementPlus from 'element-plus'
import { createApp } from 'vue'
import App from './App.vue'
import { ENTRY_KEY, sdk } from './portal'
import buildRouter from './router'

// 重写webpack的publicPath
rewriteWebpackPublicPath()

/**
 * 初始化启动参数
 * @param {import("@portal/vue-helper").AppBootArgs} args
 * @param {import("@portal/vue-helper").default} helper
 */
async function initBootArgs (token, helper) {
  // 完成SDK登录
  sdk.setAuthInfo(token)
  // 获取当前用户路由
  const routes = await helper.routes(ENTRY_KEY)
  // 获取路由映射表
  const mapping = await helper.mapping(routes)
  return {
    routes,
    mapping
  }
}

/**
 * 从基座下发的上下文数据中构造启动参数
 * @param {*} payload 基座应用下发上下文数据
 * @param {*} helper helper
 * @returns
 */
async function getBootArgs (payload, helper) {
  const profile = payload.profile
  // 完成SDK登录
  sdk.setAuthInfo(payload.token)
  // 设置sdk的profile缓存
  sdk.cache.set('app.profile', profile)
  // 构造当前用户路由
  const routes = await helper.toRoutes(profile.menus, ENTRY_KEY)
  // 获取路由映射表
  const mapping = await helper.mapping(routes)
  return {
    routes,
    mapping,
    profile
  }
}

/**
 * 启动应用
 * @param {import("@portal/vue-helper").AppBootArgs} args
 * @param {import("@portal/vue-helper").default} helper
 */
async function bootstrap (args, helper) {
  // 构造路由实例
  const basename = globalThis.__MICRO_APP_BASE_ROUTE__ || '/'
  const router = buildRouter(args.routes, basename)

  // 将helper放入window中
  globalThis.$helper = helper

  // 将router放入window中
  globalThis.$router = router

  // 监听路由的变化
  // 将$route放入window中
  router.afterEach((to) => {
    globalThis.$route = to
  })
  // 子应用路由的变化需要通知基座应用
  router.beforeEach(helper.handler.beforeRoute)

  // 注入全局样式
  helper.injectGlobalStyle()

  // 创建Vue应用实例
  const app = createApp(App)

  // 挂载路由实例
  app.use(router)

  // 挂载helper帮助上下文
  app.use(Context, {
    sdk,
    profile: args.profile
  })

  // 挂载UI组件库
  app.use(ElementPlus)

  // 挂载共享组件库
  app.use(SharedUI, {
    sdk,
    license: {
      url: ''
    }
  })

  // 将应用挂载至DOM
  app.mount('#license-app')

  // 初始化全局数据
  const startTs = Date.now()
  Promise.all([
    NameHooks.initUsers()
  ]).then(res => {
    const endTs = Date.now()
    const takeTs = endTs - startTs
    console.log('基础数据已加载完毕，共耗时：' + takeTs + 'ms')
  })

  // 仅在微前端模式下执行
  if (globalThis.microApp) {
    // 实现页面导航方法
    const navigate = (to) => {
      router.push({ ...to, name: to.key })
    }

    // 监听基座应用的消息
    const removeListener = helper.handler.listenFoundationAppEvents({
      // 处理跳转页面事件
      navigate
    })

    // 自动跳转默认页
    const page = sdk.cache.get('navigate')
    if (page) {
      navigate(page)
      sdk.cache.delete('navigate')
    }

    // 监听应用卸载事件(注意：keep-alive模式下不会触发)
    globalThis.addEventListener('unmount', function () {
      // 卸载消息监听器
      removeListener?.()
    })
  }

  // 输出「已就绪」日志
  console.log('ready')
}

/**
 * 启动入口
 */
async function start () {
  // 应用是否已启动
  let started = false

  // 构造帮助实例
  const helper = new PortalHelper(sdk, {
    loadView: (file) => import(`./pages/${file}.vue`),
    defaultOutlet: 'Default'
  })

  if (globalThis.microApp) {
    // 监听基座应用的消息
    const removeListener = helper.handler.listenFoundationAppEvents({
      navigate: (to) => {
        sdk.cache.set('navigate', to)
      },
      other: async (msg) => {
        switch (msg.type) {
          case 'resp:get-ctx': {
            // console.log('ctx信息为：', msg.payload)
            if (!started) {
              // 标记为已启动
              started = true
              // 收集参数
              const args = await getBootArgs(msg.payload, helper)
              // 启动应用
              await bootstrap(args, helper)
              // 移除消息监听
              removeListener?.()
            }
            break
          }
        }
      }
    })
    // 获取子应用上下文
    helper.dispatcher.dispatch({
      type: 'get-ctx'
    })
  } else {
    // 从url或者基座应用获取token
    const token = helper.tokenParser.parse()

    // 如果没有token，重定向至认证中心
    if (!token) {
      helper.toLogin()
      return
    }

    // 缓存token到本地
    helper.cacheStore.set(token)

    // 移除url中的token
    helper.tokenParser.remove()

    try {
      // 启动应用
      await bootstrap(await initBootArgs(token, helper), helper)
    } catch (err) {
      // 处理token过期异常
      if (err.response && err.response.status === 401) {
      // 重定向至统一认证中心
        helper.toLogin()
      }
    }
  }
}

/**
 * 启动应用
 */
start()
